// sound5/music.js: harmony, motifs and colours for the GRAVITY score.
// One key centre (D) for the whole film: minor darkness -> Lydian wonder ->
// tender major -> Phrygian gravity -> warm major resolution.
// MIDI: D1=26 D2=38 D3=50 D4=62 D5=74 D6=86 D7=98.
// `colour` = extra MIDI pitch classes (0=C .. 11=B) the shimmer may use.
import { makeRng, hashString } from './util.js';

export const PROG = {
  // after the star ignites: D add9 <-> E/D (the Lydian II, "wonder")
  bloom: {
    dur: 9, step: 0.75, atk: 0.35, rel: 1.8, amp: 0.05, reg: 'principal', colour: [8, 11],
    chords: [
      [38, 45, 50, 57, 62, 64, 66, 69],
      [38, 47, 52, 56, 59, 64, 68, 71],
    ],
  },
  // HOME: tender I - vi - IV - Vsus, a felt-piano ostinato on top (76 bpm, 8ths)
  home: {
    dur: 6.316, atk: 0.75, rel: 1.3, amp: 0.042, reg: 'flute', step: 0.3947, colour: [4, 11],
    chords: [
      [38, 50, 57, 62, 64, 66],
      [35, 47, 54, 57, 62, 66],
      [31, 43, 50, 54, 59, 66],
      [33, 45, 52, 57, 62, 64],
    ],
    motif: [
      [69, 74, 76, 78, 76, 74, 69, 0],
      [66, 71, 74, 78, 76, 74, 71, 0],
      [67, 71, 74, 78, 76, 74, 71, 0],
      [69, 74, 76, 81, 76, 74, 69, 0],
    ],
  },
  // SCALE: D Lydian, high and open
  scale: {
    dur: 7, step: 0.875, atk: 1.0, rel: 1.9, amp: 0.034, reg: 'full', colour: [8, 4, 1],
    chords: [
      [50, 57, 62, 66, 68, 73, 78],
      [50, 59, 64, 68, 71, 76, 80],
      [50, 54, 59, 61, 66, 71, 73],
      [50, 57, 61, 64, 69, 73, 76],
    ],
  },
  // GRAVITY: pedal D, minor -> Phrygian bII -> iv -> V7b9
  gravity: {
    dur: 8, step: 2, atk: 1.3, rel: 2.3, amp: 0.075, reg: 'reed', colour: [3],
    chords: [
      [38, 45, 50, 53, 57, 64],
      [38, 46, 51, 55, 58, 63],
      [38, 46, 50, 55, 58, 62],
      [38, 45, 49, 55, 58, 64],
    ],
  },
  // YOURS after the brand star is born: D6/9 <-> G(add9)/D
  yours: {
    dur: 8, step: 0.5, atk: 0.55, rel: 1.7, amp: 0.045, reg: 'principal', colour: [4, 11],
    chords: [
      [38, 45, 54, 59, 62, 64, 69],
      [38, 43, 50, 57, 59, 62, 67],
    ],
  },
  // OFFER: tonic, plagal, calm; the HOME ostinato returns at half speed
  offer: {
    dur: 12.632, atk: 1.1, rel: 2.1, step: 0.7895, amp: 0.04, reg: 'flute', colour: [4],
    chords: [
      [38, 45, 50, 54, 57, 62, 66],
      [38, 43, 50, 55, 59, 62, 67],
      [38, 45, 50, 54, 57, 62, 66],
      [38, 45, 50, 57, 62, 64, 66],
    ],
    motif: [
      [69, 74, 76, 78, 76, 74, 69, 0],
      [67, 71, 74, 79, 78, 74, 71, 0],
      [69, 74, 76, 78, 76, 74, 69, 0],
      [69, 74, 76, 78, 81, 78, 76, 0],
    ],
  },
};

// which 8th-note steps survive when the ostinato is thinned out
export const IMPORTANCE = [1, 0.35, 0.62, 0.45, 0.86, 0.3, 0.55, 0];

// the IGNITE tension cluster, in order of entry as the charge rises
export const CLUSTER = [38, 45, 50, 51, 56, 53, 57, 58, 63];

// bells for the brand star (D major pentatonic, D5..A6; kept below A6 so FM
// sidebands stay under ~8 kHz)
export const BELLS = [74, 76, 78, 81, 83, 86, 88, 90, 93];

// Organ registrations: harmonic amplitudes (8' + 4' + 2' + mixture ranks
// folded into one harmonic series).
export const REGS = {
  flute: [1, 0.1, 0.28, 0.03, 0.09, 0.02, 0.035],
  principal: [1, 0.5, 0.32, 0.3, 0.13, 0.12, 0.06, 0.09, 0.035, 0.04, 0.02, 0.03],
  reed: [1, 0.8, 0.66, 0.6, 0.56, 0.5, 0.42, 0.34, 0.28, 0.22, 0.18, 0.14, 0.11, 0.09, 0.07, 0.05],
  full: [1, 0.68, 0.42, 0.52, 0.18, 0.33, 0.09, 0.32, 0.07, 0.11, 0.045, 0.2, 0.035, 0.05, 0.025, 0.1],
};

// The brand star's motif: deterministic (the same name always gives the same
// tune), 5-7 notes of D major pentatonic that start on a chord tone, move
// mostly by step and resolve to D or A. `beats` are in eighth notes.
export function brandMotif(name) {
  const key = String(name == null ? '' : name).replace(/\s+/g, ' ').trim().toLowerCase() || 'starlex';
  const h = hashString(key);
  const rng = makeRng(h ^ 0x5bd1e995);
  const len = 5 + (h % 3);
  const START = [0, 2, 3, 5], STEPS = [-2, -1, -1, 1, 1, 2, 3, -3];
  let i = START[Math.floor(rng() * START.length)];
  const idx = [i];
  for (let k = 1; k < len - 1; k++) {
    let s = STEPS[Math.floor(rng() * STEPS.length)];
    if (i + s < 0 || i + s > 7) s = -s;
    i = Math.max(0, Math.min(7, i + s));
    if (i === idx[idx.length - 1]) i = i < 7 ? i + 1 : i - 1; // never repeat a note
    idx.push(i);
  }
  // resolve to the nearest D5 / A5 / D6 that is not the previous note
  let best = -1;
  for (const j of [0, 3, 5]) if (j !== i && (best < 0 || Math.abs(j - i) < Math.abs(best - i))) best = j;
  idx.push(best);
  const beats = idx.map((_, k) => (k === len - 1 ? 4 : rng() < 0.3 ? 2 : 1));
  return { key, notes: idx.map((j) => BELLS[j]), beats };
}
